/**
 * The PayPal card on the Settings page: the REST app customers pay invoices through.
 *
 *   * ITS OWN RECORD AND ITS OWN SAVE, like the mail account: it carries a client secret, and the
 *     tunables document is plaintext, returned to any staff user and broadcast to every agent.
 *   * THE SECRET NEVER COMES BACK. An empty box keeps the stored one and clearing it is its own tick -
 *     the three readings null (keep), "" (clear) and anything else (replace).
 *   * ONE REQUEST BUILDER FOR SAVE AND TEST, so the probe checks exactly what would be stored.
 */
import * as crm from '../../api/crm.js';
import { describeError } from '../../api/http.js';
import {
  PayPalEnvironment,
  type PayPalAccountDto,
  type PayPalTestRequest,
  type PayPalTestResult,
  type UpdatePayPalAccountRequest
} from '../../contracts.gen.js';
import { localDateTime } from '../../format.js';
import type { Card } from '../../ui/Card.js';
import { StatusLine } from '../../ui/StatusLine.js';
import {
  button,
  buttonRow,
  card,
  checkLine,
  comboBox,
  field,
  fieldRow,
  GAP,
  h2,
  muted,
  passwordBox,
  relayout,
  stackOf,
  textBox,
  vstack,
  wrappingCheck
} from '../../ui/layout.js';
import type { PageContext } from '../page.js';

/** `PaymentLimits.Currencies` - a static readonly array, so ContractsGen cannot carry it. */
export const PAYPAL_CURRENCIES = [
  'AUD', 'BRL', 'CAD', 'CHF', 'CZK', 'DKK', 'EUR', 'GBP', 'HKD', 'HUF', 'ILS', 'JPY', 'MXN',
  'MYR', 'NOK', 'NZD', 'PHP', 'PLN', 'SEK', 'SGD', 'THB', 'TWD', 'USD'
] as const;

/** In `PayPalEnvironment` order. */
const ENVIRONMENTS = ['Sandbox (testing - no real money)', 'Live'] as const;

export interface PayPalSection {
  card: Card;
  load(): Promise<void>;
}

/** Where the account is read and written: the organization's own, or the host's. */
export interface PayPalApi {
  get(signal?: AbortSignal): Promise<PayPalAccountDto>;
  update(req: UpdatePayPalAccountRequest): Promise<PayPalAccountDto>;
  test(req: PayPalTestRequest): Promise<PayPalTestResult>;
}

export interface PayPalSectionOptions {
  api?: PayPalApi;
  enableLabel?: string;
  description?: string;
  /** Who pays through it, for the ready line: "Customers" or "Organizations". */
  payer?: string;
}

const ORGANIZATION_PAYPAL: PayPalApi = {
  get: signal => crm.getPayPalAccount(signal),
  update: req => crm.updatePayPalAccount(req),
  test: req => crm.testPayPal(req)
};

export function payPalSection(ctx: PageContext, canEdit: boolean, options: PayPalSectionOptions = {}): PayPalSection {
  const api = options.api ?? ORGANIZATION_PAYPAL;
  const payer = options.payer ?? 'Customers';
  const enabled = wrappingCheck(options.enableLabel ?? 'Let customers pay invoices with PayPal');
  const environment = comboBox<string>(ENVIRONMENTS, { selectedIndex: PayPalEnvironment.Sandbox });
  const clientId = textBox({ placeholder: 'From the app on developer.paypal.com' });
  const secret = passwordBox();
  const secretState = muted('');
  const clearSecret = wrappingCheck('Clear the stored secret');
  const currency = comboBox<string>(PAYPAL_CURRENCIES, { selectedIndex: PAYPAL_CURRENCIES.indexOf('USD') });
  const brandName = textBox({ placeholder: 'Shown to the customer at PayPal - defaults to your sender name' });
  const ready = muted('');
  const updated = muted('');

  const status = new StatusLine();
  const saveButton = button('Save PayPal settings', () => void save(), true);
  const testButton = button('Test connection', () => void test());

  for (const c of [enabled, environment, clientId, secret, clearSecret, currency, brandName, saveButton, testButton]) {
    c.enabled = canEdit;
  }

  const shell = card();
  shell.controls.add(
    stackOf(
      vstack({ spacing: GAP.md }),
      h2('PayPal payments'),
      muted(
        options.description ??
          'Every issued invoice gets a pay link: put {PayLine} or {PayLink} in the invoice message on the Mail tab (the shipped message already has it). The customer pays at PayPal and the invoice is marked paid the moment the payment completes. The secret is stored encrypted and is never sent back to this page.'
      ),
      checkLine(enabled),
      field('PayPal environment', environment),
      fieldRow(field('Client ID', clientId), field('Secret', secret)),
      secretState,
      checkLine(clearSecret),
      fieldRow(field('Currency', currency), field('Brand name (optional)', brandName)),
      muted(
        'Use the currency your invoices are written in - the amount is charged exactly as invoiced, with no conversion. Create a REST app under Apps & Credentials on developer.paypal.com; sandbox and live apps have different IDs and secrets. Pay links use the address in Agent:PublicUrl, which customers must be able to reach.'
      ),
      ready,
      updated,
      stackOf(buttonRow(), saveButton, testButton),
      status.label
    )
  );

  function bind(a: PayPalAccountDto): void {
    enabled.checked = a.enabled;
    environment.selectedIndex = a.environment;
    clientId.text = a.clientId ?? '';
    secret.text = '';
    clearSecret.checked = false;
    const index = PAYPAL_CURRENCIES.indexOf((a.currency ?? 'USD') as (typeof PAYPAL_CURRENCIES)[number]);
    currency.selectedIndex = index >= 0 ? index : PAYPAL_CURRENCIES.indexOf('USD');
    brandName.text = a.brandName ?? '';
    secretState.text = a.hasClientSecret
      ? 'A secret is stored. Leave the box empty to keep it.'
      : 'No secret stored yet.';

    if (a.ready) {
      ready.text =
        a.environment === PayPalEnvironment.Live
          ? `${payer} can pay issued invoices with PayPal.`
          : `${payer} can pay issued invoices - in the PayPal sandbox, so no real money moves. Switch to Live when you are ready.`;
      ready.role = a.environment === PayPalEnvironment.Live ? 'ok' : 'warn';
    } else {
      ready.text = a.enabled ? 'Not ready: PayPal needs both the client ID and the secret.' : 'PayPal payments are off.';
      ready.role = a.enabled ? 'danger' : 'muted';
    }
    updated.text = a.updatedUtc ? `Last changed ${localDateTime(a.updatedUtc)}${a.updatedBy ? ` by ${a.updatedBy}` : ''}.` : '';
    updated.visible = updated.text.length > 0;
    relayout(shell);
  }

  function buildRequest(): UpdatePayPalAccountRequest {
    const typed = secret.text.trim();
    return {
      enabled: enabled.checked,
      environment: environment.selectedIndex as PayPalEnvironment,
      clientId: clientId.text.trim(),
      clientSecret: clearSecret.checked ? '' : typed.length > 0 ? typed : null,
      currency: PAYPAL_CURRENCIES[currency.selectedIndex] ?? 'USD',
      brandName: brandName.text.trim()
    };
  }

  function setBusy(busy: boolean): void {
    saveButton.enabled = canEdit && !busy;
    testButton.enabled = canEdit && !busy;
  }

  async function save(): Promise<void> {
    setBusy(true);
    status.busy('Saving…');
    try {
      const saved = await api.update(buildRequest());
      if (ctx.cancelled()) return;
      bind(saved);
      status.ok('PayPal settings saved.');
    } catch (error) {
      if (!ctx.cancelled()) status.error('Could not save the PayPal settings: ' + describeError(error));
    } finally {
      setBusy(false);
    }
  }

  async function test(): Promise<void> {
    setBusy(true);
    status.busy('Asking PayPal…');
    const result = await api.test({ settings: buildRequest() });
    setBusy(false);
    if (ctx.cancelled()) return;
    if (result.success) status.ok(result.message);
    else status.error(result.message);
  }

  return {
    card: shell,
    async load() {
      try {
        const a = await api.get(ctx.dispose.signal());
        if (!ctx.cancelled()) bind(a);
      } catch (error) {
        if (!ctx.cancelled()) status.error('Could not load the PayPal settings: ' + describeError(error));
      }
    }
  };
}
